import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Flatpickr from "react-flatpickr";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

import BreadCrumb from "common/breadcrumb";
import LoadingComponent from "common/components/loader";

import { Feature, SubscriptionFeature, SubscriptionPlan } from "models/subscription/subcription-plan";
import { GetFeatures, AddSubscriptionPlan, UpdateSubscriptionPlan, GetSubscriptionPlan } from "api/tenant";
import { AppUtility } from "utility/app-utility";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  displayName: Yup.string().required("Display name is required"),
  subcriptionValue: AppUtility.formikCurrencyValidtion(),
  validNoOfDays: Yup.number().default(null).nullable().typeError("Invalid number"), // Ensures input is a number,
  startDate: AppUtility.formikDateValidtion(),
  endDate: AppUtility.formikDateValidtion(),
  discountStartDate: AppUtility.formikDateValidtion(),
  discountEndDate: AppUtility.formikDateValidtion(),
});

interface ISubscriptionPlan {
  id?: string;
  name?: string;
  displayName?: string;
  description?: string;

  validNoOfDays?: string | null;
  startDate?: Date | null;
  endDate?: Date | null;
  subcriptionValue?: string;
  isActive?: boolean;
  // enableDiscount?: boolean;
  discountAmount?: string;
  isDiscountPercentage?: boolean;
  discountStartDate?: Date | null;
  discountEndDate?: Date | null;
  features?: SubscriptionFeature[];
}
interface Props {
  subscription?: SubscriptionPlan;
}

const SubscriptionManagePage: React.FC<Props> = ({ subscription }) => {
  const title = "Subscription";
  const pageTitle = "Manage";
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [selectedPlan, setSelectedPlan] = useState<ISubscriptionPlan>();
  const [editMode, setEditMode] = useState(false);

  const [features, setFeatures] = useState<SubscriptionFeature[]>([]);

  useEffect(() => {
    if ((id ?? "") == "") {
      setEditMode(false);
      loadFeatures();
    } else {
      loadPlan(id!);
      setEditMode(true);
    }
  }, []);

  const loadFeatures = async () => {
    setLoading(true);
    const res = await GetFeatures();
    if (!(res?.status?.isError ?? false) && res?.data) {
      let _st = (res?.data ?? []).filter((f: Feature) => f.isActive ?? false);
      const _mapF: SubscriptionFeature[] = (res?.data ?? []).map((f) => ({
        enabled: true,
        feature: {
          id: f.id,
          name: f.name,
        },
      }));
      setFeatures(_mapF);
    } else {
      setFeatures([]);
    }
    setLoading(false);
  };
  const loadPlan = async (_id: string) => {
    setLoading(true);
    const res = await GetSubscriptionPlan(_id);
    if (!(res?.status?.isError ?? false) && res?.data) {
      loadExistingData(res?.data);
    } else {
      setError("Invalid Subscription Plan");
    }
    setLoading(false);
  };

  const getInitialValue = () => {
    if (selectedPlan?.id) {
      const _d = {
        ...selectedPlan,
        validNoOfDays: (selectedPlan.validNoOfDays ?? "") == "" ? null : selectedPlan.validNoOfDays,
      };
      return _d;
    } else {
      return {
        name: "",
        displayName: "",
        description: "",
        isActive: true,

        validNoOfDays: 30,
        startDate: null,
        endDate: null,
        subcriptionValue: "",

        //enableDiscount: true,
        discountAmount: "",
        isDiscountPercentage: true,
        discountStartDate: null,
        discountEndDate: null,
        features: [],
        // ...selectedPlan, // Spread the fetched plan data if available
      };
    }
  };
  const formik: any = useFormik({
    initialValues: getInitialValue(),
    validationSchema: validationSchema,
    // Validation function to check the entire form
    validate: async (values: typeof formik.values) => {
      try {
        await validationSchema.validate(values, { abortEarly: false });
        return {}; // Return empty object when validation succeeds
      } catch (err: any) {
        // Log errors to your database here
        console.error("Validation errors:", err.errors);
        // handleLogErrorToDB(errors.inner); // Custom function to log errors to DB
        return err.inner.reduce((errorObj: any, currentError: any) => {
          return {
            ...errorObj,
            [currentError.path]: currentError.message,
          };
        }, {} as Record<string, string>);
      }
    },

    onSubmit: async (values) => {
      try {
        setError("");
        setSuccess("");
        setSaving(true);

        var _plan: SubscriptionPlan = {
          id: values?.id ?? undefined,
          name: values?.name ?? "",
          displayName: values?.displayName ?? "",
          description: values?.description ?? "",
          isActive: values?.isActive,

          validNoOfDays: (values.validNoOfDays ?? "") != "" ? parseInt(values.validNoOfDays!) : undefined,
          startDate:
            (values?.startDate ?? "") != "" ? AppUtility.ISODateToISOString(values.startDate!, true) : undefined,
          endDate: (values?.endDate ?? "") != "" ? AppUtility.ISODateToISOString(values.endDate!, true) : undefined,
          subcriptionValue: (values?.subcriptionValue ?? "") != "" ? parseFloat(values?.subcriptionValue) : undefined,

          // enableDiscount: selectedPlan?.enableDiscount,
          discountAmount: (values?.discountAmount ?? "") != "" ? parseFloat(values?.discountAmount) : undefined,
          isDiscountAmount: !(values?.isDiscountPercentage ?? false),
          discountStartDate:
            (values?.discountStartDate ?? "") != ""
              ? AppUtility.ISODateToISOString(values.discountStartDate!, true)
              : undefined,
          discountEndDate:
            (values?.discountEndDate ?? "") != ""
              ? AppUtility.ISODateToISOString(values.discountEndDate!, true)
              : undefined,
          features: features,
        };
        const res = !editMode ? await AddSubscriptionPlan(_plan) : await UpdateSubscriptionPlan(_plan.id ?? "", _plan);

        if (!(res?.status?.isError ?? false) && (res?.data?.id ?? "") != "") {
          setSuccess("success");
          // setSelectedPlan(res!.data!);
          //onTenantSaveSuccess(res!.data!);
          // setEditMode(true);\
          if (!editMode) {
            setSelectedPlan(getInitialValue());
            formik.resetForm();
          } else {
            loadExistingData(res.data!);
          }
        } else {
          setError(`error: ${res?.status?.errorCode ?? "500"}:${res?.status?.errorMessage ?? ""}`);
        }
        setTimeout(() => {
          setError("");
          setSuccess("");
        }, 3000);

        setSaving(false);
      } catch (error) {
        setError("Something went wrong...");
        setSaving(false);
      }
    },
  });

  useEffect(() => {
    loadExistingData(subscription);
  }, [subscription]);

  const loadExistingData = (sub?: SubscriptionPlan) => {
    if (sub) {
      let _obj = {
        id: sub?.id ?? "",
        name: sub?.name ?? "",
        displayName: sub?.displayName ?? "",
        description: sub?.description ?? "",
        isActive: sub?.isActive,

        validNoOfDays: (sub?.validNoOfDays ?? "").toString() == "" ? null : sub?.validNoOfDays?.toString(),
        startDate: (sub?.startDate ?? "") != "" ? AppUtility.ISOStringToDate(sub.startDate!) : null,
        endDate: (sub?.endDate ?? "") != "" ? AppUtility.ISOStringToDate(sub.endDate!) : null,
        subcriptionValue: (sub?.subcriptionValue ?? "").toString(),

        // enableDiscount: sub?.enableDiscount,
        discountAmount: (sub?.discountAmount ?? "").toString(),
        isDiscountPercentage: !(sub?.isDiscountAmount ?? false),
        discountStartDate:
          (sub?.discountStartDate ?? "") != "" ? AppUtility.ISOStringToDate(sub.discountStartDate!) : null,
        discountEndDate: (sub?.discountEndDate ?? "") != "" ? AppUtility.ISOStringToDate(sub.discountEndDate!) : null,
        features: sub?.features ?? [],
      };
      setSelectedPlan(_obj);
      formik.setValues(_obj); // Set formik values when tenant prop changes
      //setEditMode((sub?.id ?? "").length > 0);
      if ((sub?.features ?? []).length > 0) {
        setFeatures(sub?.features ?? []);
      } else {
        setFeatures([]);
      }
    }
  };

  const onFeatureChanged = (item: SubscriptionFeature, e: any) => {
    const _ft = [...(features ?? [])];
    var _ety = _ft.find((f) => f.feature?.id == item.feature?.id);
    if (_ety) {
      _ety.enabled = e.target.checked;
    }
    setFeatures(_ft);
  };
  return (
    <React.Fragment>
      <BreadCrumb title={`${editMode ? "Update" : "Add"} Plan`} pageTitle={"Subscription"} />
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-x-5">
        <div className="xl:col-span-12">
          <div className="card">
            <div className="card-body">
              {loading ? (
                <LoadingComponent></LoadingComponent>
              ) : (
                <>
                  <form onSubmit={formik.handleSubmit}>
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-4 xl:grid-cols-12">
                      <div className="xl:col-span-8">
                        <div className="grid grid-cols-1 gap-5 grid-cols-12">
                          <div className="xl:col-span-6">
                            <div>
                              <label htmlFor="name" className="inline-block mb-2 text-base font-medium">
                                Name <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                id="name"
                                name="name"
                                className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 ${
                                  formik.errors.name ? "border-red-500" : ""
                                }`}
                                onChange={formik.handleChange}
                                value={formik.values.name}
                              />
                              {formik.touched.name && formik.errors.name && (
                                <p className="text-red-500 text-sm mt-1">{formik.errors.name}</p>
                              )}
                            </div>
                          </div>
                          <div className="xl:col-span-6">
                            <div>
                              <label htmlFor="displayName" className="inline-block mb-2 text-base font-medium">
                                Display Name<span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                id="displayName"
                                name="displayName"
                                className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 ${
                                  formik.errors.displayName ? "border-red-500" : ""
                                }`}
                                onChange={formik.handleChange}
                                value={formik.values.displayName}
                              />
                              {formik.touched.displayName && formik.errors.displayName && (
                                <p className="text-red-500 text-sm mt-1">{formik.errors.displayName}</p>
                              )}
                            </div>
                          </div>
                          <div className="xl:col-span-6">
                            <div>
                              <label htmlFor="description" className="inline-block mb-2 text-base font-medium">
                                Description
                              </label>
                              <input
                                type="text"
                                id="description"
                                name="description"
                                className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 ${
                                  formik.errors.description ? "border-red-500" : ""
                                }`}
                                onChange={formik.handleChange}
                                value={formik.values.description}
                              />
                              {formik.touched.description && formik.errors.description && (
                                <p className="text-red-500 text-sm mt-1">{formik.errors.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="xl:col-span-6">
                            <div>
                              <label htmlFor="subcriptionValue" className="inline-block mb-2 text-base font-medium">
                                Subcription Value
                              </label>
                              <input
                                type="text"
                                name="subcriptionValue"
                                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                placeholder="subcriptionValue"
                                value={formik.values.subcriptionValue}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              />
                              {formik.touched.subcriptionValue && formik.errors.subcriptionValue ? (
                                <p id="subcriptionValue" className="mt-1 text-sm text-red-500">
                                  {formik.errors.subcriptionValue}
                                </p>
                              ) : null}
                            </div>
                          </div>

                          <div className="xl:col-span-6">
                            <div>
                              <label htmlFor="validNoOfDays" className="inline-block mb-2 text-base font-medium">
                                Valid No of Days
                              </label>
                              <input
                                type="text"
                                id="validNoOfDays"
                                name="validNoOfDays"
                                className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 ${
                                  formik.errors.validNoOfDays ? "border-red-500" : ""
                                }`}
                                onChange={formik.handleChange}
                                value={formik.values.validNoOfDays}
                              />
                              {formik.touched.validNoOfDays && formik.errors.validNoOfDays && (
                                <p className="text-red-500 text-sm mt-1">{formik.errors.validNoOfDays}</p>
                              )}
                            </div>
                          </div>
                          <div className="xl:col-span-6"></div>
                          <div className="xl:col-span-6">
                            <div>
                              <label htmlFor="startDate" className="inline-block mb-2 text-base font-medium">
                                Start Date <span className="text-red-500">*</span>
                              </label>
                              <Flatpickr
                                id="startDate"
                                className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 ${
                                  formik.errors.startDate ? "border-red-500" : ""
                                }`}
                                options={{
                                  dateFormat: "d-M-Y",
                                }}
                                placeholder="Select Date"
                                onChange={(date) => formik.setFieldValue("startDate", date[0])}
                                value={formik.values.startDate}
                              />
                              {formik.touched.startDate && formik.errors.startDate && (
                                <p className="text-red-500 text-sm mt-1">{formik.errors.startDate}</p>
                              )}
                            </div>
                          </div>
                          <div className="xl:col-span-6">
                            <div>
                              <label htmlFor="endDate" className="inline-block mb-2 text-base font-medium">
                                End Date <span className="text-red-500">*</span>
                              </label>
                              <Flatpickr
                                id="endDate"
                                className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 ${
                                  formik.errors.endDate ? "border-red-500" : ""
                                }`}
                                options={{
                                  dateFormat: "d-M-Y",
                                }}
                                placeholder="Select Date"
                                onChange={(date) => formik.setFieldValue("endDate", date[0])}
                                value={formik.values.endDate}
                              />
                              {formik.touched.endDate && formik.errors.endDate && (
                                <p className="text-red-500 text-sm mt-1">{formik.errors.endDate}</p>
                              )}
                            </div>
                          </div>
                          <div className="xl:col-span-6">
                            <div>
                              <label htmlFor="isActive" className="inline-block mb-2 text-base font-medium">
                                Active
                              </label>
                              <div className="flex items-center">
                                <div className="relative inline-block w-10 align-middle transition duration-200 ease-in ltr:mr-2 rtl:ml-2">
                                  <input
                                    type="checkbox"
                                    name="isActive"
                                    id="isActive"
                                    className="absolute block size-5 transition duration-300 ease-linear border-2 rounded-full appearance-none cursor-pointer border-slate-200 dark:border-zink-500 bg-white/80 dark:bg-zink-400 peer/published checked:bg-white dark:checked:bg-white ltr:checked:right-0 rtl:checked:left-0 checked:bg-none checked:border-green-500 dark:checked:border-green-500 arrow-none"
                                    onChange={formik.handleChange}
                                    checked={formik.values.isActive}
                                  />
                                  <label
                                    htmlFor="isActive"
                                    className="block h-5 overflow-hidden duration-300 ease-linear border rounded-full cursor-pointer cursor-pointertransition border-slate-200 dark:border-zink-500 bg-slate-200 dark:bg-zink-600 peer-checked/published:bg-green-500 peer-checked/published:border-green-500"
                                  ></label>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="mb-4 xl:col-span-6">
                            <div>
                              <label htmlFor="isDiscountPercentage" className="inline-block mb-2 text-base font-medium">
                                Discount Percentage
                              </label>
                              <div className="flex items-center flex-row">
                                <div className="relative inline-block w-10 align-middle transition duration-200 ease-in ltr:mr-2 rtl:ml-2">
                                  <input
                                    type="checkbox"
                                    name="isDiscountPercentage"
                                    id="isDiscountPercentage"
                                    className="absolute block size-5 transition duration-300 ease-linear border-2 rounded-full appearance-none cursor-pointer border-slate-200 dark:border-zink-500 bg-white/80 dark:bg-zink-400 peer/published checked:bg-white dark:checked:bg-white ltr:checked:right-0 rtl:checked:left-0 checked:bg-none checked:border-green-500 dark:checked:border-green-500 arrow-none"
                                    onChange={formik.handleChange}
                                    checked={formik.values.isDiscountPercentage}
                                  />
                                  <label
                                    htmlFor="isDiscountPercentage"
                                    className="block h-5 overflow-hidden duration-300 ease-linear border rounded-full cursor-pointer cursor-pointertransition border-slate-200 dark:border-zink-500 bg-slate-200 dark:bg-zink-600 peer-checked/published:bg-green-500 peer-checked/published:border-green-500"
                                  ></label>
                                </div>
                                <input
                                  type="text"
                                  name="discountAmount"
                                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                  placeholder="Value"
                                  value={formik.values.discountAmount}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                />
                                {formik.touched.discountAmount && formik.errors.discountAmount ? (
                                  <p id="discountAmount" className="mt-1 text-sm text-red-500">
                                    {formik.errors.discountAmount}
                                  </p>
                                ) : null}
                              </div>
                            </div>
                          </div>

                          <div className="xl:col-span-6">
                            <div>
                              <label htmlFor="discountStartDate" className="inline-block mb-2 text-base font-medium">
                                Discount Valid From
                              </label>
                              <Flatpickr
                                id="discountStartDate"
                                className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 ${
                                  formik.errors.discountStartDate ? "border-red-500" : ""
                                }`}
                                options={{
                                  dateFormat: "d-M-Y",
                                }}
                                placeholder="Select Date"
                                onChange={(date) => formik.setFieldValue("discountStartDate", date[0])}
                                value={formik.values.discountStartDate}
                              />
                              {formik.touched.discountStartDate && formik.errors.discountStartDate && (
                                <p className="text-red-500 text-sm mt-1">{formik.errors.discountStartDate}</p>
                              )}
                            </div>
                          </div>
                          <div className="xl:col-span-6">
                            <div>
                              <label htmlFor="discountEndDate" className="inline-block mb-2 text-base font-medium">
                                Discount Valid To
                              </label>
                              <Flatpickr
                                id="endDate"
                                className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 ${
                                  formik.errors.discountEndDate ? "border-red-500" : ""
                                }`}
                                options={{
                                  dateFormat: "d-M-Y",
                                }}
                                placeholder="Select Date"
                                onChange={(date) => formik.setFieldValue("discountEndDate", date[0])}
                                value={formik.values.discountEndDate}
                              />
                              {formik.touched.discountEndDate && formik.errors.discountEndDate && (
                                <p className="text-red-500 text-sm mt-1">{formik.errors.discountEndDate}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="xl:col-span-4">
                        <div className="grid grid-cols-1 gap-5 grid-cols-1">
                          <div>Features</div>
                          {loading ? (
                            <LoadingComponent />
                          ) : (
                            (features ?? []).map((item, index) => (
                              <div className="flex items-center gap-2" key={`feature-${index}`}>
                                <input
                                  id="checkboxSoft2"
                                  className="size-4 border border-green-200 rounded-sm appearance-none bg-green-50 checked:bg-green-500 checked:border-green-500 dark:border-green-800 dark:bg-green-500/20 dark:checked:bg-green-500 dark:checked:border-green-500"
                                  type="checkbox"
                                  value=""
                                  onChange={(e) => onFeatureChanged(item, e)}
                                  checked={item?.enabled ?? false}
                                />
                                <label htmlFor="checkboxSoft2" className="align-middle">
                                  {item?.feature?.name ?? ""}
                                </label>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                      <button
                        type="reset"
                        className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-700 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
                        onClick={formik.handleReset}
                      >
                        Reset
                      </button>
                      <button
                        type="submit"
                        className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                      >
                        {editMode ? "Update" : "Create"}
                      </button>
                    </div>
                  </form>
                  {saving && <LoadingComponent />}
                  {success && (
                    <div
                      className="px-4 py-3 mt-3 text-sm text-green-500 border border-green-200 rounded-md bg-green-50 dark:bg-green-400/20 dark:border-green-500/50"
                      id="successAlert"
                    >
                      <b>Success! </b>
                      {!editMode ? (
                        <>Subscription plan has been created. Now you can add users to the system</>
                      ) : (
                        <> Subscription plan has been updated. </>
                      )}
                    </div>
                  )}
                  {error && (
                    <div
                      className="px-4 py-3 mt-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50"
                      id="successAlert"
                    >
                      {!editMode ? (
                        <>
                          Oops! Something went wrong while attempting to create subscription plan. Please try again
                          later or contact support for assistance.
                        </>
                      ) : (
                        <>
                          Oops! Something went wrong while attempting to update subscription plan. Please try again
                          later or contact support for assistance.
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default SubscriptionManagePage;
